"use client";

import type * as React from "react";

import { clsx } from "clsx";
import { TabList } from "@ui/components/core/TabList";
import { Children, useMemo, useState } from "react";
import { TabPanel, Tabs, type Key } from "react-aria-components";

import type { PageSectionHorizontalPadding } from "./pageSectionPadding";

type TabAlignment = "left" | "center" | "right";
type TabHorizontalPadding = PageSectionHorizontalPadding;

const tabListHorizontalPaddingClasses: Record<TabHorizontalPadding, string> = {
  default: "tw-px-4 md:tw-px-12",
  small: "tw-px-2 md:tw-px-4",
  none: "tw-px-0",
};

interface BlockTabbedBlocksClientProps {
  tabs: Array<{
    id: string;
    label: string;
    /** Pre-rendered icon element from server */
    icon?: React.ReactNode;
  }>;
  alignment?: null | TabAlignment;
  horizontalPadding?: null | TabHorizontalPadding;
  title: string;
  children: React.ReactNode;
}

export function BlockTabbedBlocksClient({
  tabs,
  alignment = "center",
  horizontalPadding = "none",
  title,
  children,
}: BlockTabbedBlocksClientProps) {
  const [selectedKey, setSelectedKey] = useState<Key>(tabs[0]?.id ?? "");
  const panels = useMemo(() => Children.toArray(children), [children]);
  const effectiveHorizontalPadding = horizontalPadding ?? "none";

  if (tabs.length === 0) {
    return null;
  }

  if (tabs.length === 1) {
    return <>{panels[0] ?? null}</>;
  }

  return (
    <Tabs selectedKey={selectedKey} onSelectionChange={setSelectedKey} className='tw-flex tw-flex-col'>
      <div className='tw-container'>
        <div className={tabListHorizontalPaddingClasses[effectiveHorizontalPadding]}>
          <TabList
            aria-label={title}
            items={tabs.map(tab => ({ id: tab.id, title: tab.label, icon: tab.icon }))}
            centered={alignment === "center"}
            className={clsx(alignment === "center" && "tw-mx-auto", alignment === "right" && "tw-ml-auto")}
            size='lg'
          />
        </div>
      </div>
      <>
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab.id}
            id={tab.id}
            shouldForceMount
            className='tw-outline-none focus-visible:tw-outline-focus'
          >
            {({ isInert }) => (
              <div hidden={isInert} aria-hidden={isInert}>
                {panels[index] ?? null}
              </div>
            )}
          </TabPanel>
        ))}
      </>
    </Tabs>
  );
}
